import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import axios from "axios";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { FloatingButtons } from "./components/FloatingButtons";
import { AuthProvider } from "./context/AuthContext";
import { ServiceProvider } from "./context/ServiceContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import Home from "./pages/Home";
import ServiceDetail from "./pages/ServiceDetail";
import About from "./pages/About";
import Contact from "./pages/Contact";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Component to track page visits
const PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const trackVisit = async () => {
      // Don't track admin pages
      if (location.pathname.startsWith('/admin')) return;

      try {
        await axios.post(`${API}/analytics/track`, {
          page: location.pathname,
          service_id: null
        });
      } catch (error) {
        // Silent fail for analytics
      }
    };

    trackVisit();
  }, [location.pathname]);

  return null;
};

// Component to handle scrolling to anchors (e.g. #services)
const ScrollToAnchor = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 0);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return null;
};

// Layout for public pages (with Navbar, Footer, FloatingButtons)
const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    {children}
    <Footer />
    <FloatingButtons />
  </>
);

function App() {
  return (
    <AuthProvider>
      <ServiceProvider>
        <div className="App">
          <BrowserRouter>
            <PageTracker />
            <ScrollToAnchor />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/service/:serviceId" element={<PublicLayout><ServiceDetail /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />

              {/* Admin Routes */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </BrowserRouter>
        </div>
      </ServiceProvider>
    </AuthProvider>
  );
}

export default App;
